import React from 'react';
import { getProductIconSVG } from '../icons.js';
import { iconPairs } from '../data.js';

function IconItem({ icon, index, onDragStart, onDragEnd, isUsed, isHighlighted, isCorrectHint, leftIcon, isDraggingDisabled }) {
    const handleDragStart = (e) => {
        if (isUsed || isDraggingDisabled) {
            e.preventDefault();
            return;
        }
        onDragStart(e);
    };
    
    const handleTouchStart = (e) => {
        if (isUsed || isDraggingDisabled) {
            e.preventDefault();
            return;
        }
        
        // Prevent scrolling while dragging
        e.preventDefault();
        
        const touch = e.touches[0];
        const element = e.currentTarget;
        const rect = element.getBoundingClientRect();
        
        // Create a clone of the icon to follow the finger
        const clone = element.cloneNode(true);
        clone.id = 'touch-drag-clone';
        clone.classList.remove('highlighted'); // Remove highlight from clone
        clone.classList.remove('correct-hint'); // Remove correct hint from clone
        clone.style.position = 'fixed';
        clone.style.width = '45px';
        clone.style.height = '45px';
        clone.style.left = `${touch.clientX - 22.5}px`;
        clone.style.top = `${touch.clientY - 22.5}px`;
        clone.style.pointerEvents = 'none';
        clone.style.zIndex = '10000';
        clone.style.opacity = '0.8';
        clone.style.transform = 'scale(1.1)';
        clone.style.transition = 'none';
        clone.style.animation = 'none';
        document.body.appendChild(clone);
        
        // Store the clone reference
        element.dragClone = clone;
        
        // Create a synthetic event object for touch
        const syntheticEvent = {
            currentTarget: element,
            preventDefault: () => {},
            dataTransfer: {
                effectAllowed: '',
                setData: () => {}
            }
        };
        
        onDragStart(syntheticEvent);
        
        // Store touch start position
        element.touchStartX = touch.clientX;
        element.touchStartY = touch.clientY;
    };
    
    const handleTouchMove = (e) => {
        if (isUsed) return;
        
        e.preventDefault();
        
        const touch = e.touches[0];
        const element = e.currentTarget;
        const clone = element.dragClone;
        
        if (clone) {
            // Move the clone to follow the finger
            clone.style.left = `${touch.clientX - 22.5}px`;
            clone.style.top = `${touch.clientY - 22.5}px`;
        }
        
        // Check if touching over a sphere and show correct/incorrect indicator
        const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);
        let sphere = elementAtPoint;
        while (sphere && !sphere.classList.contains('sphere')) {
            sphere = sphere.parentElement;
        }
        
        // Remove previous indicators
        const leftSphere = document.querySelector('.product-sphere');
        const rightSphere = document.querySelector('.drop-sphere');
        const leftSphereIcon = document.getElementById('left-sphere-icon');
        const rightSphereIcon = document.getElementById('right-sphere-icon');
        
        if (leftSphere) {
            leftSphere.classList.remove('drag-over-correct', 'drag-over-incorrect', 'drag-over-active');
        }
        if (rightSphere) {
            rightSphere.classList.remove('drag-over-correct', 'drag-over-incorrect');
        }
        
        // Reset left sphere icon preview if not hovering over it
        if (leftSphereIcon && !leftIcon) {
            leftSphereIcon.style.backgroundImage = 'none';
        }
        
        // Show indicator and preview if over left sphere (when it's empty)
        if (sphere && sphere.classList.contains('product-sphere') && !leftIcon) {
            sphere.classList.add('drag-over-active');
            // Show preview of the icon in the left sphere
            if (leftSphereIcon) {
                leftSphereIcon.style.backgroundImage = `url('./images/SVG/${icon.iconName}.svg')`;
            }
        }
        
        // Show indicator if over right sphere and left sphere has an icon
        if (sphere && sphere.classList.contains('drop-sphere') && leftIcon) {
            // Check if it's a valid pair
            const draggedIcon = icon;
            const pair = iconPairs.find(p => p.id === draggedIcon.productId);
            const isCorrectPair = pair && leftIcon && (
                (pair.icon1 === leftIcon.iconName && pair.icon2 === draggedIcon.iconName) ||
                (pair.icon2 === leftIcon.iconName && pair.icon1 === draggedIcon.iconName)
            );
            
            if (isCorrectPair) {
                sphere.classList.add('drag-over-correct');
            } else {
                sphere.classList.add('drag-over-incorrect');
            }
        }
    };
    
    const handleTouchEnd = (e) => {
        if (isUsed) return;
        
        const touch = e.changedTouches[0];
        const element = e.currentTarget;
        const clone = element.dragClone;
        
        // Remove the clone
        if (clone) {
            clone.remove();
            element.dragClone = null;
        }
        
        // Remove drag-over indicators
        const leftSphere = document.querySelector('.product-sphere');
        const rightSphere = document.querySelector('.drop-sphere');
        const leftSphereIcon = document.getElementById('left-sphere-icon');
        
        if (leftSphere) {
            leftSphere.classList.remove('drag-over-correct', 'drag-over-incorrect', 'drag-over-active');
        }
        if (rightSphere) {
            rightSphere.classList.remove('drag-over-correct', 'drag-over-incorrect');
        }
        
        const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);
        
        // Find the sphere element (traverse up if needed)
        let dropTarget = elementAtPoint;
        while (dropTarget && !dropTarget.classList.contains('sphere')) {
            dropTarget = dropTarget.parentElement;
        }
        
        // If not dropping on left sphere or drop fails, clear the preview
        if (!dropTarget || !dropTarget.classList.contains('product-sphere')) {
            if (leftSphereIcon && !leftIcon) {
                leftSphereIcon.style.backgroundImage = 'none';
            }
        }
        
        if (dropTarget) {
            // Create synthetic drop event
            const syntheticEvent = {
                preventDefault: () => {},
                stopPropagation: () => {},
                currentTarget: dropTarget,
                dataTransfer: {
                    dropEffect: 'move'
                }
            };
            
            // Trigger the drop event on the sphere
            if (dropTarget.classList.contains('product-sphere')) {
                const dropEvent = new Event('customDrop');
                dropEvent.syntheticEvent = syntheticEvent;
                dropTarget.dispatchEvent(dropEvent);
            } else if (dropTarget.classList.contains('drop-sphere')) {
                const dropEvent = new Event('customDrop');
                dropEvent.syntheticEvent = syntheticEvent;
                dropTarget.dispatchEvent(dropEvent);
            }
        }
        
        onDragEnd();
    };
    
    return (
        <div 
            className={`category-icon icon-appear ${isUsed ? 'used' : ''} ${isHighlighted ? 'highlighted' : ''} ${isCorrectHint ? 'correct-hint' : ''} ${isDraggingDisabled ? 'dragging-disabled' : ''}`}
            draggable={!isUsed && !isDraggingDisabled ? "true" : "false"} 
            data-icon-id={icon.id}
            data-icon-name={icon.iconName}
            data-product-id={icon.productId}
            style={{ 
                // animationDelay: `${index * 0.02}s`,
                backgroundImage: `url('./images/SVG/${icon.iconName}.svg')`,
                // backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
            onDragStart={handleDragStart}
            onDragEnd={onDragEnd}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
        </div>
    );
}

export function CategoryIcons({ visibleIcons, usedIcons, onDragStart, onDragEnd, highlightedIconId, correctIconHintId, leftIcon, isDraggingDisabled }) {
    // Calculate how many placeholders we need to maintain 10 slots
    const totalSlots = 10;
    const actualIconsCount = visibleIcons?.length || 0;
    const placeholderCount = Math.max(0, totalSlots - actualIconsCount);
    
    return (
        <div className="instruction-box">
            <div className="instruction-text">DRAG AND DROP TO REVEAL</div>
            <div className="category-icons">
                {visibleIcons?.map((icon, index) => (
                    <IconItem
                        key={icon.id}
                        icon={icon}
                        index={index}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        isUsed={usedIcons?.includes(icon.id)}
                        isHighlighted={icon.id === highlightedIconId}
                        isCorrectHint={icon.id === correctIconHintId}
                        leftIcon={leftIcon}
                        isDraggingDisabled={isDraggingDisabled}
                    />
                ))}
                {/* Add placeholder dashes to maintain visual consistency */}
                {Array.from({ length: placeholderCount }).map((_, index) => (
                    <div 
                        key={`placeholder-${index}`}
                        className="category-icon-placeholder"
                        style={{ 
                            animationDelay: `${(actualIconsCount + index) * 0.02}s`
                        }}
                    >
                        -
                    </div>
                ))}
            </div>
        </div>
    );
}
